import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  Checkbox,
  Input,
  FormControl,
  FormLabel,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  IconButton,
  useDisclosure,
  Container,
  Heading,
  Stack,
  useBreakpointValue,
  Badge,
  useColorModeValue,
  useColorMode,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  RadioGroup,
  Radio,
  Divider,
  Textarea,
  Tooltip,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import {
  DashboardDataTable,
  useDashboardTableStyles,
} from "../utils/DashboardDataTable";
import {
  AddIcon,
  EditIcon,
  DeleteIcon,
  BellIcon,
  ViewIcon,
} from "@chakra-ui/icons";
import { FiCalendar, FiCheckCircle, FiMic, FiShare2, FiVolumeX } from "react-icons/fi";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { hitBeApi } from "../../../api/api";
import {
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import TabCacheButton from "../../common/TabCacheButton";
import { VoiceAssistantMicButton } from "../../common/VoiceAssistantMicButton";
import {
  getSpeechRecognitionClass,
  getPreferredSttLang,
  startBrowserVoiceDictation,
} from "../../../utils/browserSpeechRecognition";
import { speakBrowserAssistantReply } from "../../../utils/browserSpeechSpeak";
import { useNavigate } from "react-router-dom";
import { appStore } from "../../../store/Store";
import ShareCollaborationModal from "../../common/ShareCollaborationModal";

const STRUCTURE_LOCK_DAYS = 3;

/** Month + year for stats labels (IST, matches chore dates). */
function formatStatsMonthLabel(d) {
  const dt = d instanceof Date && !Number.isNaN(d.getTime()) ? d : new Date();
  return new Intl.DateTimeFormat("en-IN", {
    month: "long",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(dt);
}

/** YYYY-MM-DD on the Asia/Kolkata calendar (matches server daily-chore logic). */
function toYmd(d) {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  return x.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

function calendarDaysBetweenYmd(earlierYmd, laterYmd) {
  const a = new Date(`${earlierYmd}T12:00:00+05:30`);
  const b = new Date(`${laterYmd}T12:00:00+05:30`);
  return Math.round((b - a) / 86400000);
}

/** Next IST calendar date after ymd — matches server stats iteration. */
function nextYmdIst(ymd) {
  const d = new Date(`${ymd}T12:00:00+05:30`);
  d.setTime(d.getTime() + 86400000);
  return d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
}

/** Inclusive count of IST days from start through end (YYYY-MM-DD). */
function countInclusiveIstDaysBetween(startYmd, endYmd) {
  if (!startYmd || !endYmd || startYmd > endYmd) return 0;
  let n = 0;
  for (let y = startYmd; y <= endYmd; y = nextYmdIst(y)) {
    n += 1;
  }
  return n;
}

/** Inclusive end IST date for scoring (never beyond today — matches API). */
function effectiveStatsInclusiveEnd(startYmd, endYmd) {
  const t = toYmd(new Date());
  if (!startYmd || !endYmd || startYmd > endYmd) return null;
  if (startYmd > t) return null;
  const endCap = endYmd <= t ? endYmd : t;
  if (startYmd > endCap) return null;
  return endCap;
}

function emptyDailyChoreStatsResponse() {
  return {
    totalTasks: 0,
    totalCompleted: 0,
    pendingTotal: 0,
    completionRate: 0,
    calendarDaysInRange: 0,
    trackedRows: 0,
    dailyStats: [],
  };
}

function mergeDailyChoreStatsPayload(data, startYmd, endYmd) {
  const d = data && typeof data === "object" ? data : {};
  const clientCal = countInclusiveIstDaysBetween(startYmd, endYmd);
  const serverCal = Number(d.calendarDaysInRange);
  const cal =
    Number.isFinite(serverCal) && serverCal > 0 ? serverCal : clientCal;
  return {
    ...d,
    calendarDaysInRange: cal,
    trackedRows: d.trackedRows ?? (d.dailyStats || []).length,
  };
}

function isFutureDay(d) {
  return toYmd(d) > toYmd(new Date());
}

/** Non-negative when selected is today or in the past (IST calendar). */
function daysFromSelectedToToday(selected) {
  return calendarDaysBetweenYmd(toYmd(selected), toYmd(new Date()));
}

function canEditChoreStructure(selectedDate) {
  if (isFutureDay(selectedDate)) return false;
  return daysFromSelectedToToday(selectedDate) <= STRUCTURE_LOCK_DAYS;
}

/** Same window as structure edits: today and the previous STRUCTURE_LOCK_DAYS calendar days (IST). */
function canToggleCompletion(selectedDate) {
  return canEditChoreStructure(selectedDate);
}

function isTodayYmd(selectedDate) {
  return toYmd(selectedDate) === toYmd(new Date());
}

function chartXAxisIntervalByLength(len) {
  if (!len || len <= 0) return 0;
  if (len <= 16) return 0;
  if (len <= 45) return 1;
  if (len <= 90) return 2;
  if (len <= 180) return 4;
  return 6;
}

function chartWidthForPoints(points, baseWidth) {
  if (!points || points <= 0) return baseWidth;
  const pxPerPoint =
    points > 900 ? 6 : points > 600 ? 7 : points > 360 ? 8 : points > 180 ? 10 : 12;
  return Math.max(baseWidth, points * pxPerPoint);
}

function chartHeightForPoints(points, baseHeight, maxHeight = 820) {
  if (!points || points <= 0) return baseHeight;
  const extra = Math.min(maxHeight - baseHeight, Math.floor(points / 3));
  return Math.max(baseHeight, Math.min(maxHeight, baseHeight + extra));
}

function percentile(values, p) {
  if (!Array.isArray(values) || values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(
    sorted.length - 1,
    Math.max(0, Math.floor((sorted.length - 1) * p))
  );
  return sorted[idx];
}

const DAILY_CHORE_ANALYTICS_RANGES = [
  { id: "30d", label: "30 days", days: 30 },
  { id: "60d", label: "60 days", days: 60 },
  { id: "90d", label: "90 days", days: 90 },
  { id: "6m", label: "6 months", days: 180 },
  { id: "all", label: "All time", days: null },
  { id: "custom", label: "Custom", days: null },
];

/** Daily chore API may send `index` as number or string — avoid === mismatches. */
function choreRowIndexEqual(a, b) {
  if (a === undefined || a === null || b === undefined || b === null) return false;
  return Number(a) === Number(b);
}

function reindexReminderConfigs(configs, removeIndex) {
  const rm = Number(removeIndex);
  return (configs || [])
    .filter((r) => !choreRowIndexEqual(r.index, rm))
    .map((r) => {
      const rid = r.reminderId?._id || r.reminderId;
      const idx = Number(r.index);
      return {
        index: idx > rm ? idx - 1 : idx,
        reminderId: rid,
      };
    });
}

/** Build form state from populated ReminderConfig (or plain object from API). */
function reminderDocToForm(reminderId) {
  if (reminderId == null) {
    return { type: "", time: "09:00" };
  }
  if (typeof reminderId === "string") {
    return { type: "", time: "09:00", _id: reminderId };
  }
  const raw =
    typeof reminderId.toObject === "function"
      ? reminderId.toObject()
      : { ...reminderId };
  const _id = raw._id ?? raw.id;
  let time = raw.time != null ? String(raw.time) : "09:00";
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (m) {
    time = `${String(m[1]).padStart(2, "0")}:${m[2]}`;
  }
  return {
    ...raw,
    _id,
    type: raw.type || "",
    time: time || "09:00",
    dayOfWeek: raw.dayOfWeek || "",
    dayOfMonth:
      raw.dayOfMonth != null && !Number.isNaN(Number(raw.dayOfMonth))
        ? Number(raw.dayOfMonth)
        : "",
  };
}

/** Align per-chore notes with list length (matches server daily chore shape). */
function padChoreNotes(len, notes) {
  const n = Math.max(0, Number(len) || 0);
  const src = Array.isArray(notes) ? notes.map((x) => String(x ?? "")) : [];
  const out = src.slice(0, n);
  while (out.length < n) out.push("");
  return out;
}

const DailyChoreManager = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [chores, setChores] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [choreNotes, setChoreNotes] = useState([]);
  const [reminderConfigs, setReminderConfigs] = useState([]);
  const [recurringTemplates, setRecurringTemplates] = useState([]);
  const [metaInferred, setMetaInferred] = useState(false);
  const [editingChore, setEditingChore] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const shareChoreModal = useDisclosure();
  const [primaryDailyChoreDocId, setPrimaryDailyChoreDocId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const choreBoxBg = useColorModeValue("white", "gray.800");
  const recurringBoxBg = useColorModeValue("teal.50", "gray.700");
  const badgeColorScheme = "blue";
  const choreAssistantCardBg = useColorModeValue("white", "gray.800");
  const choreAssistantCardBorder = useColorModeValue("gray.200", "gray.600");
  const choreAssistantHeading = useColorModeValue("blue.600", "blue.300");
  const choreAssistantSubtle = useColorModeValue("gray.600", "gray.400");
  const choreAssistantInputBg = useColorModeValue("gray.50", "gray.900");
  const choreAssistantFocus = useColorModeValue("blue.500", "blue.300");
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const surfaceCard = useColorModeValue("white", "gray.800");
  const mutedLine = useColorModeValue("gray.100", "gray.700");
  const assistantHeaderBg = useColorModeValue("gray.50", "gray.900");
  const statCardBg = useColorModeValue("gray.50", "gray.900");
  const choreRowDoneBorder = useColorModeValue("green.400", "green.500");
  const choreRowTodoBorder = useColorModeValue("blue.400", "blue.300");
  const recurringRowBg = useColorModeValue("white", "gray.800");
  const emptyStateBg = useColorModeValue("gray.50", "gray.900");
  const inferredNoteBg = useColorModeValue("blue.50", "whiteAlpha.50");
  const inferredNoteBorder = useColorModeValue("blue.400", "blue.500");
  const choreChartAxis = useColorModeValue("#64748b", "#94a3b8");
  const choreChartGrid = useColorModeValue("#e2e8f033", "#ffffff14");
  const { colorMode } = useColorMode();
  const tableStyles = useDashboardTableStyles();
  const {
    isOpen: isAnalyticsOpen,
    onOpen: onAnalyticsOpen,
    onClose: onAnalyticsClose,
  } = useDisclosure();
  const {
    isOpen: isDailyTrendOpen,
    onOpen: onDailyTrendOpen,
    onClose: onDailyTrendClose,
  } = useDisclosure();
  const [dailyTrendRange, setDailyTrendRange] = useState("30d");
  const [dailyTrendCustomStart, setDailyTrendCustomStart] = useState("");
  const [dailyTrendCustomEnd, setDailyTrendCustomEnd] = useState("");
  const [dailyTrendStats, setDailyTrendStats] = useState(null);
  const [analyticsStats, setAnalyticsStats] = useState(null);
  const [analyticsPreset, setAnalyticsPreset] = useState("week");
  const [customStart, setCustomStart] = useState(
    () => toYmd(new Date(Date.now() - 7 * 86400000))
  );
  const [customEnd, setCustomEnd] = useState(() => toYmd(new Date()));

  const [removingAllReminders, setRemovingAllReminders] = useState(false);
  const [choreVoiceDraft, setChoreVoiceDraft] = useState("");
  const [choreVoiceReply, setChoreVoiceReply] = useState("");
  const [choreVoiceLoading, setChoreVoiceLoading] = useState(false);
  const [choreVoiceListening, setChoreVoiceListening] = useState(false);
  const [choreVoiceSpeaking, setChoreVoiceSpeaking] = useState(false);
  const submitChoreVoiceAssistantRef = useRef(async () => {});

  const containerWidth = useBreakpointValue({
    base: "95%",
    md: "90%",
    lg: "container.lg",
  });

  const fetchRecurringTemplates = useCallback(async () => {
    try {
      const response = await hitBeApi("daily-chore/recurring", "GET");
      if (response.success) {
        setRecurringTemplates(response.data || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchMonthlyStats = useCallback(async () => {
    try {
      const d =
        selectedDate instanceof Date && !Number.isNaN(selectedDate.getTime())
          ? selectedDate
          : new Date();
      const startDate = new Date(d.getFullYear(), d.getMonth(), 1);
      const endDate = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      const formattedStartDate = toYmd(startDate);
      const formattedEndDate = toYmd(endDate);
      const cappedEnd =
        effectiveStatsInclusiveEnd(formattedStartDate, formattedEndDate);

      if (!cappedEnd) {
        setMonthlyStats(emptyDailyChoreStatsResponse());
        return;
      }

      const response = await hitBeApi(
        `daily-chore/stats?startDate=${formattedStartDate}&endDate=${cappedEnd}&statsV=carry`,
        "GET"
      );

      if (response.success) {
        setMonthlyStats(
          mergeDailyChoreStatsPayload(
            response.data,
            formattedStartDate,
            cappedEnd
          )
        );
      }
    } catch (error) {
      console.error("Error fetching monthly stats:", error);
    }
  }, [selectedDate]);

  const statsMonthCaption = useMemo(
    () => formatStatsMonthLabel(selectedDate),
    [selectedDate]
  );

  const choreDailyTrendSeries = useMemo(() => {
    const raw = monthlyStats?.dailyStats;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((row) => {
      const dt = row?.date ? new Date(row.date) : null;
      const dayLabel =
        dt instanceof Date &&
        !Number.isNaN(dt.getTime())
          ? dt.toLocaleDateString("en-IN", {
              timeZone: "Asia/Kolkata",
              month: "short",
              day: "numeric",
            })
          : "";
      return {
        dayLabel,
        totalTasks: Number(row.totalTasks) || 0,
        completedTasks: Number(row.completedTasks) || 0,
      };
    });
  }, [monthlyStats]);

  const dailyTrendSeries = useMemo(() => {
    const raw = dailyTrendStats?.dailyStats;
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw.map((row) => {
      const dt = row?.date ? new Date(row.date) : null;
      const dayLabel =
        dt instanceof Date && !Number.isNaN(dt.getTime())
          ? dt.toLocaleDateString("en-IN", {
              timeZone: "Asia/Kolkata",
              month: "short",
              day: "numeric",
            })
          : "";
      return {
        dayLabel,
        totalTasks: Number(row.totalTasks) || 0,
        completedTasks: Number(row.completedTasks) || 0,
      };
    });
  }, [dailyTrendStats]);

  const dailyTrendDisplayMax = useMemo(() => {
    if (!dailyTrendSeries.length) return 0;
    const values = [];
    dailyTrendSeries.forEach((row) => {
      values.push(Number(row.totalTasks) || 0);
      values.push(Number(row.completedTasks) || 0);
    });
    const p95 = percentile(values, 0.95);
    return Math.max(p95, 1);
  }, [dailyTrendSeries]);

  const dailyTrendChartHeight = useMemo(
    () => chartHeightForPoints(dailyTrendSeries.length, 420, 1200),
    [dailyTrendSeries.length]
  );

  useEffect(() => {
    fetchChores(selectedDate);
    fetchMonthlyStats();
    fetchRecurringTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, fetchMonthlyStats]);

  const fetchChores = async (date) => {
    try {
      if (!(date instanceof Date) || isNaN(date)) {
        return;
      }

      setIsLoading(true);
      const formattedDate = toYmd(date);

      const response = await hitBeApi(`daily-chore/${formattedDate}`, "GET");

      if (response.success) {
        const { data } = response;
        const rawId = data?._id;
        setPrimaryDailyChoreDocId(
          rawId != null && String(rawId) !== "undefined"
            ? String(rawId)
            : null
        );
        const list = data.chores || [];
        setChores(list);
        setStatuses(data.statuses || Array(list.length).fill(false));
        setChoreNotes(padChoreNotes(list.length, data.choreNotes));
        const rawRc = data.reminderConfigs || [];
        setReminderConfigs(
          rawRc.map((r) => ({
            index: Number.isFinite(Number(r?.index)) ? Number(r.index) : 0,
            reminderId: r?.reminderId,
          }))
        );
        setMetaInferred(!!data.inferredFromPrevious);
      } else {
        throw new Error(response.message || "Failed to fetch chores");
      }
    } catch (error) {
      setPrimaryDailyChoreDocId(null);
      if (error.message?.includes("Unauthorized")) {
        navigate("/");
        document.cookie =
          "token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
        toast({
          title: "Error",
          description: "Unauthorized: Invalid token, please login once",
          status: "error",
          duration: 2000,
          isClosable: true,
        });
        return;
      }
      toast({
        title: "Error fetching data",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const handleStatusChange = async (index) => {
    if (!canToggleCompletion(selectedDate)) {
      toast({
        title: "Cannot update completion",
        description: isFutureDay(selectedDate)
          ? "You cannot track completion for future days."
          : `You can only check chores off for today and the previous ${STRUCTURE_LOCK_DAYS} days.`,
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      const newStatuses = [...statuses];
      newStatuses[index] = !newStatuses[index];
      setStatuses(newStatuses);

      const formattedDate = toYmd(selectedDate);
      const response = await hitBeApi(`daily-chore/${formattedDate}`, "PUT", {
        chores,
        statuses: newStatuses,
        choreNotes: padChoreNotes(chores.length, choreNotes),
        reminderConfigs,
      });

      if (response.success) {
        toast({
          title: "Status updated",
          status: "success",
          duration: 2000,
        });
        fetchMonthlyStats();
      } else {
        throw new Error(response.message || "Failed to update status");
      }
    } catch (error) {
      toast({
        title: "Error updating status",
        description: error.message,
        status: "error",
        duration: 3000,
      });
      const newStatuses = [...statuses];
      newStatuses[index] = !newStatuses[index];
      setStatuses(newStatuses);
    }
  };

  const handleChoreEdit = (index) => {
    if (!canEditChoreStructure(selectedDate)) {
      toast({
        title: "Cannot edit",
        description: `Chore names and reminders can only be changed for today and the previous ${STRUCTURE_LOCK_DAYS} days.`,
        status: "warning",
        duration: 4000,
      });
      return;
    }

    const choreName = chores[index];
    const status = statuses[index];
    const reminders = reminderConfigs.filter((reminder) =>
      choreRowIndexEqual(reminder.index, index)
    );
    const rawRem = reminders.length > 0 ? reminders[0]?.reminderId : null;
    setEditingChore({
      index,
      choreName,
      choreNote: String(choreNotes[index] ?? ""),
      status,
      reminder: reminderDocToForm(rawRem),
    });
    onOpen();
  };

  const handleChoreDelete = async (index) => {
    if (!canEditChoreStructure(selectedDate)) {
      toast({
        title: "Cannot delete",
        description: `You can only remove chores for today and the previous ${STRUCTURE_LOCK_DAYS} days.`,
        status: "warning",
        duration: 4000,
      });
      return;
    }

    try {
      const deleted = reminderConfigs.filter((r) => choreRowIndexEqual(r.index, index));
      if (deleted.length > 0 && deleted[0]?.reminderId?._id) {
        await hitBeApi(
          `reminder-config/${deleted[0].reminderId._id}`,
          "DELETE"
        );
      }

      const newChores = chores.filter((_, i) => i !== index);
      const newStatuses = statuses.filter((_, i) => i !== index);
      const newChoreNotes = choreNotes.filter((_, i) => i !== index);
      const newReminderConfigs = reindexReminderConfigs(reminderConfigs, index);

      const formattedDate = toYmd(selectedDate);
      await hitBeApi(`daily-chore/${formattedDate}`, "PUT", {
        chores: newChores,
        statuses: newStatuses,
        choreNotes: padChoreNotes(newChores.length, newChoreNotes),
        reminderConfigs: newReminderConfigs,
      });

      // Reload from the server instead of setting local state from stripped configs:
      // that kept only bare reminder ids, so the next edit opened as "No reminder" and
      // silently dropped the reminder. A refetch keeps reminderConfigs populated + aligned.
      await fetchChores(selectedDate);

      toast({
        title: "Chore deleted",
        status: "success",
        duration: 2000,
      });
      fetchMonthlyStats();
    } catch (error) {
      toast({
        title: "Error deleting chore",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleChoreSubmit = async (choreData) => {
    if (!canEditChoreStructure(selectedDate)) {
      toast({
        title: "Cannot save",
        description: `Edits are limited to today and the previous ${STRUCTURE_LOCK_DAYS} days.`,
        status: "warning",
        duration: 4000,
      });
      return;
    }

    try {
      const { choreName, choreNote, reminder, status, applyRecurring } = choreData;
      const editSnap = editingChore;
      const isEditingRow =
        editSnap &&
        editSnap.index != null &&
        editSnap.index !== "" &&
        !Number.isNaN(Number(editSnap.index));
      const editIndex = isEditingRow ? Number(editSnap.index) : null;

      const noteClipped = String(choreNote ?? "")
        .trim()
        .slice(0, 4000);
      let reminderConfig = null;

      if (reminder?.type) {
        const reminderData = {
          type: reminder.type,
          time: reminder.time || "09:00",
          choreName,
        };
        if (reminder.type === "weekly" && reminder.dayOfWeek) {
          reminderData.dayOfWeek = reminder.dayOfWeek;
        }
        if (
          reminder.type === "monthly" &&
          reminder.dayOfMonth != null &&
          reminder.dayOfMonth !== "" &&
          !Number.isNaN(Number(reminder.dayOfMonth))
        ) {
          reminderData.dayOfMonth = Number(reminder.dayOfMonth);
        }

        if (reminder._id) {
          reminderConfig = await hitBeApi(
            `reminder-config/${reminder._id}`,
            "PUT",
            reminderData
          );
        } else {
          reminderConfig = await hitBeApi("reminder-config", "POST", reminderData);
        }
      }

      const formattedDate = toYmd(selectedDate);

      const newChores =
        isEditingRow && editIndex !== null
          ? chores.map((c, i) => (i === editIndex ? choreName : c))
          : [...chores, choreName];

      const newStatuses =
        isEditingRow && editIndex !== null
          ? [...statuses]
          : [...statuses, status || false];

      const targetIndex =
        isEditingRow && editIndex !== null ? editIndex : chores.length;

      let newChoreNotes;
      if (isEditingRow && editIndex !== null) {
        newChoreNotes = chores.map((_, i) =>
          i === editIndex ? noteClipped : String(choreNotes[i] ?? "")
        );
      } else {
        newChoreNotes = [...choreNotes.map((x) => String(x ?? "")), noteClipped];
      }
      newChoreNotes = padChoreNotes(newChores.length, newChoreNotes);

      const stripRc = (rc) =>
        (rc || []).map((r) => ({
          index: r.index,
          reminderId: r.reminderId?._id || r.reminderId,
        }));

      let newReminderConfigs = stripRc(reminderConfigs);

      if (reminder?.type) {
        const newId = reminderConfig?.data?._id || reminderConfig?.data;
        newReminderConfigs = newReminderConfigs.filter(
          (r) => !choreRowIndexEqual(r.index, targetIndex)
        );
        newReminderConfigs.push({ index: targetIndex, reminderId: newId });
      } else {
        newReminderConfigs = newReminderConfigs.filter(
          (r) => !choreRowIndexEqual(r.index, targetIndex)
        );
      }

      await hitBeApi(`daily-chore/${formattedDate}`, "PUT", {
        chores: newChores,
        statuses: newStatuses,
        choreNotes: newChoreNotes,
        reminderConfigs: newReminderConfigs,
      });

      if (applyRecurring && isTodayYmd(selectedDate)) {
        const rid = reminderConfig?.data?._id || reminderConfig?.data;
        const next = [
          ...recurringTemplates,
          {
            choreName,
            reminderConfigId: rid || undefined,
          },
        ];
        await hitBeApi("daily-chore/recurring", "PUT", { templates: next });
        await fetchRecurringTemplates();
      }

      onClose();
      setEditingChore(null);

      // Reload from the server rather than trusting the stripped local configs: those hold
      // only bare reminder ids, which made a follow-up edit load as "No reminder" and drop
      // the reminder on save. Refetching keeps reminderConfigs populated so the bell sticks.
      await fetchChores(selectedDate);

      toast({
        title: "Chore saved",
        status: "success",
        duration: 2000,
      });
      fetchMonthlyStats();
    } catch (error) {
      toast({
        title: "Error saving chore",
        description: error.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const removeRecurringTemplate = async (idx) => {
    try {
      const next = recurringTemplates.filter((_, i) => i !== idx);
      await hitBeApi("daily-chore/recurring", "PUT", { templates: next });
      await fetchRecurringTemplates();
      await fetchChores(selectedDate);
      toast({ title: "Updated recurring chores", status: "success", duration: 2000 });
    } catch (e) {
      toast({
        title: "Error",
        description: e.message,
        status: "error",
        duration: 3000,
      });
    }
  };

  const loadAnalytics = async () => {
    const today = new Date();
    let start;
    let end = new Date(today);
    end.setHours(0, 0, 0, 0);

    if (analyticsPreset === "week") {
      start = new Date(today);
      start.setDate(today.getDate() - 6);
    } else if (analyticsPreset === "month") {
      start = new Date(today);
      start.setDate(today.getDate() - 29);
    } else {
      start = new Date(customStart);
      end = new Date(customEnd);
    }

    const formattedStartDate = toYmd(start);
    const formattedEndDate = toYmd(end);
    const cappedEnd = effectiveStatsInclusiveEnd(
      formattedStartDate,
      formattedEndDate
    );

    if (!cappedEnd) {
      setAnalyticsStats(emptyDailyChoreStatsResponse());
      return;
    }

    try {
      const response = await hitBeApi(
        `daily-chore/stats?startDate=${formattedStartDate}&endDate=${cappedEnd}&statsV=carry`,
        "GET"
      );
      if (response.success) {
        setAnalyticsStats(
          mergeDailyChoreStatsPayload(
            response.data,
            formattedStartDate,
            cappedEnd
          )
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  const loadDailyTrend = async () => {
    const today = new Date();
    let start;
    let end = new Date(today);
    end.setHours(0, 0, 0, 0);

    const range = DAILY_CHORE_ANALYTICS_RANGES.find(
      (r) => r.id === dailyTrendRange
    );
    if (range?.id === "custom") {
      start = new Date(dailyTrendCustomStart);
      end = new Date(dailyTrendCustomEnd);
    } else if (range?.days) {
      start = new Date(today);
      start.setDate(today.getDate() - (range.days - 1));
    } else {
      start = new Date("2000-01-01T00:00:00");
    }

    const formattedStartDate = toYmd(start);
    const formattedEndDate = toYmd(end);
    const cappedEnd = effectiveStatsInclusiveEnd(
      formattedStartDate,
      formattedEndDate
    );

    if (!cappedEnd) {
      setDailyTrendStats(emptyDailyChoreStatsResponse());
      return;
    }

    try {
      const response = await hitBeApi(
        `daily-chore/stats?startDate=${formattedStartDate}&endDate=${cappedEnd}&statsV=carry`,
        "GET"
      );
      if (response.success) {
        setDailyTrendStats(
          mergeDailyChoreStatsPayload(
            response.data,
            formattedStartDate,
            cappedEnd
          )
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (isAnalyticsOpen) {
      loadAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnalyticsOpen, analyticsPreset, customStart, customEnd]);

  useEffect(() => {
    if (isDailyTrendOpen) {
      loadDailyTrend();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDailyTrendOpen, dailyTrendRange, dailyTrendCustomStart, dailyTrendCustomEnd]);

  const handleRemoveAllReminders = async () => {
    if (
      !window.confirm(
        "Remove every email reminder for daily chores? Your task names and lists stay the same; only reminders are cleared."
      )
    ) {
      return;
    }
    try {
      setRemovingAllReminders(true);
      const response = await hitBeApi("daily-chore/reminders", "DELETE");
      if (response.success) {
        toast({
          title: "Reminders removed",
          description: "All daily-chore email reminders have been cleared.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        await fetchChores(selectedDate);
        await fetchRecurringTemplates();
      } else {
        throw new Error(response.message || "Failed to remove reminders");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setRemovingAllReminders(false);
    }
  };

  const handleDeleteAll = async () => {
    try {
      const response = await hitBeApi("daily-chore/delete-all", "DELETE");
      if (response.success) {
        toast({
          title: "Success",
          description: "All daily chores have been deleted",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        setChores([]);
        setStatuses([]);
        setChoreNotes([]);
        setReminderConfigs([]);
        setRecurringTemplates([]);
        fetchMonthlyStats();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        setSelectedDate(today);
      } else {
        throw new Error(response.message || "Failed to delete all chores");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const stopChoreAssistantSpeech = useCallback(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setChoreVoiceSpeaking(false);
  }, []);

  const speakChoreAssistantReply = useCallback((replyText) => {
    if (typeof window === "undefined" || !replyText) return;
    try {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
      speakBrowserAssistantReply(replyText, {
        lang: "en-IN",
        flushDelayMs: 0,
        onBeforeSpeak: () => setChoreVoiceSpeaking(true),
        onEnd: () => setChoreVoiceSpeaking(false),
        onError: () => setChoreVoiceSpeaking(false),
      });
    } catch {
      setChoreVoiceSpeaking(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined" && window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const runChoreVoiceCaptureOnce = () => {
    stopChoreAssistantSpeech();
    startBrowserVoiceDictation({
      lang: getPreferredSttLang(),
      onListeningStart: () => setChoreVoiceListening(true),
      onInterim: (t) => setChoreVoiceDraft(t),
      onListeningEnd: () => setChoreVoiceListening(false),
      onFinal: (t) => {
        setChoreVoiceDraft(t);
        submitChoreVoiceAssistantRef.current(t);
      },
      onError: () => {},
    });
  };

  const submitChoreVoiceAssistant = async (overrideText) => {
    const raw =
      overrideText !== undefined && overrideText !== null
        ? String(overrideText)
        : choreVoiceDraft;
    const text = raw.trim();
    if (!text) {
      toast({
        title: "Nothing to send",
        description: "Type or dictate a command for your chores.",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    stopChoreAssistantSpeech();
    setChoreVoiceLoading(true);
    try {
      const viewDate = toYmd(selectedDate);
      const res = await hitBeApi("daily-chore/voice-assistant", "POST", {
        text,
        viewDate,
      });
      if (res.success && res.data) {
        if (res.data.userTokenDetails) {
          const currentUser = appStore.getState().user || {};
          appStore.dispatch({
            type: "SET_USER",
            value: { ...currentUser, userTokenDetails: res.data.userTokenDetails },
          });
        }
        const reply = res.data.reply || "";
        setChoreVoiceReply(reply);
        if (res.data.actions?.length) {
          await fetchChores(selectedDate);
          fetchMonthlyStats();
          fetchRecurringTemplates();
        }
        const hasMutation = res.data.actions?.length > 0;
        toast({
          title: hasMutation ? "Chores updated" : "Assistant",
          description: reply || "Done.",
          status: hasMutation ? "success" : "info",
          duration: 4500,
          isClosable: true,
        });
        if (reply) {
          speakChoreAssistantReply(reply);
        }
      }
    } catch (error) {
      toast({
        title: "Assistant error",
        description: error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setChoreVoiceLoading(false);
    }
  };

  submitChoreVoiceAssistantRef.current = submitChoreVoiceAssistant;

  const startChoreVoiceRecognition = () => {
    if (!getSpeechRecognitionClass()) {
      toast({
        title: "Voice not supported",
        description: "Use Chrome or Edge, or type below.",
        status: "info",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (choreVoiceLoading || choreVoiceListening) return;
    runChoreVoiceCaptureOnce();
  };

  const structureLocked = !canEditChoreStructure(selectedDate);
  const completionAllowed = canToggleCompletion(selectedDate);

  const formattedSelectedDate = selectedDate.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  });

  const openAddChore = () => {
    if (!canEditChoreStructure(selectedDate)) {
      toast({
        title: "Cannot add chore",
        description: `You can only add or edit chores for today and the previous ${STRUCTURE_LOCK_DAYS} days.`,
        status: "warning",
        duration: 4000,
      });
      return;
    }
    setEditingChore(null);
    onOpen();
  };

  return (
    <>
    <Box bg={pageBg} minH="100vh" py={{ base: 4, md: 8 }}>
      <Container maxW={containerWidth} px={{ base: 3, md: 6 }}>
        <VStack spacing={{ base: 5, md: 7 }} align="stretch">
          <Card
            overflow="hidden"
            borderRadius="2xl"
            boxShadow="lg"
            borderWidth="1px"
            borderColor={choreAssistantCardBorder}
            bg={surfaceCard}
          >
            <Box
              bgGradient="linear(to-r, blue.500, cyan.500)"
              px={{ base: 5, md: 8 }}
              py={{ base: 5, md: 7 }}
            >
              <Flex
                direction={{ base: "column", sm: "row" }}
                justify="space-between"
                align={{ base: "stretch", sm: "center" }}
                gap={4}
              >
                <Box>
                  <Heading
                    as="h1"
                    size="lg"
                    color="white"
                    fontWeight="extrabold"
                    letterSpacing="tight"
                  >
                    Daily chores
                  </Heading>
                  <Text
                    color="whiteAlpha.900"
                    fontSize="sm"
                    mt={1}
                    maxW="lg"
                    lineHeight="tall"
                  >
                    Plan tasks, optional email reminders, and voice commands — times use the
                    Asia/Kolkata calendar.
                  </Text>
                </Box>
                <HStack
                  spacing={2}
                  alignSelf={{ base: "stretch", sm: "center" }}
                  flexWrap="wrap"
                  justify={{ base: "stretch", sm: "flex-end" }}
                >
                  <TabCacheButton tabId="daily-chore" onGradient size="md" />
                  <Tooltip
                    label={
                      primaryDailyChoreDocId
                        ? "Invite others — same chore list for this calendar day"
                        : "Add or save chores for this day first so the list exists on the server"
                    }
                    hasArrow
                    openDelay={300}
                  >
                    <Button
                      leftIcon={<Icon as={FiShare2} />}
                      colorScheme="whiteAlpha"
                      bg="whiteAlpha.200"
                      _hover={{ bg: "whiteAlpha.300" }}
                      size={{ base: "md", sm: "md" }}
                      isDisabled={!primaryDailyChoreDocId}
                      onClick={shareChoreModal.onOpen}
                    >
                      Share day
                    </Button>
                  </Tooltip>
                  <Button
                    leftIcon={<ViewIcon />}
                    colorScheme="whiteAlpha"
                    bg="whiteAlpha.200"
                    _hover={{ bg: "whiteAlpha.300" }}
                    size={{ base: "md", sm: "md" }}
                    flex="1"
                    onClick={() => {
                      loadAnalytics();
                      onAnalyticsOpen();
                    }}
                  >
                    Analytics
                  </Button>
                </HStack>
              </Flex>
            </Box>
          </Card>

          <Alert status="info" borderRadius="xl" variant="subtle" py={3}>
            <AlertIcon />
            <Box>
              <AlertTitle fontSize="sm">Editing window</AlertTitle>
              <AlertDescription fontSize="sm">
                Future days are read-only. You can add, edit, remove chores and reminders,
                and check items off only for{" "}
                <strong>today and the previous {STRUCTURE_LOCK_DAYS} days</strong> (IST).
              </AlertDescription>
            </Box>
          </Alert>

        <Card
          w="full"
          borderRadius="xl"
          borderWidth="1px"
          borderColor={choreAssistantCardBorder}
          bg={choreAssistantCardBg}
          boxShadow="sm"
          overflow="hidden"
        >
          <CardHeader
            pb={2}
            borderBottomWidth="1px"
            borderColor={mutedLine}
            bg={assistantHeaderBg}
          >
            <HStack spacing={3}>
              <Icon as={FiMic} color={choreAssistantHeading} boxSize={5} />
              <Box>
                <Text fontWeight="semibold" fontSize="sm" color={choreAssistantHeading}>
                  Voice &amp; text assistant
                </Text>
                <Text fontSize="xs" color={choreAssistantSubtle}>
                  Applies to the day selected in the calendar below.
                </Text>
              </Box>
            </HStack>
          </CardHeader>
          <CardBody px={{ base: 3, md: 5 }} py={{ base: 4, md: 5 }}>
          <Text fontSize="xs" color={choreAssistantSubtle} mb={3} lineHeight="tall">
            Examples: &quot;List my chores&quot;, &quot;Add chore water plants&quot;, &quot;Mark
            dishes as done&quot;, &quot;Rename gym to workout&quot;, &quot;Delete the old
            chore&quot;. Reminders are set in the chore dialog, not by voice.
          </Text>
          <Textarea
            value={choreVoiceDraft}
            onChange={(e) => setChoreVoiceDraft(e.target.value)}
            placeholder='Example: "What chores do I have today?"'
            size="sm"
            rows={3}
            bg={choreAssistantInputBg}
            borderColor={choreAssistantCardBorder}
            _focus={{
              borderColor: choreAssistantFocus,
              boxShadow: "outline",
            }}
            mb={3}
          />
          <HStack spacing={3} flexWrap="wrap" align="center">
            <Button
              size="sm"
              colorScheme="blue"
              onClick={() => submitChoreVoiceAssistant()}
              isLoading={choreVoiceLoading}
              loadingText="Sending"
            >
              Send to assistant
            </Button>
            <VoiceAssistantMicButton
              colorScheme="blue"
              tooltipLabel="Browser speech (Chrome / Edge). Sends text to the server."
              onClick={startChoreVoiceRecognition}
              isLoading={choreVoiceListening}
              isDisabled={choreVoiceLoading}
            />
            {choreVoiceSpeaking ? (
              <Button
                size="sm"
                variant="outline"
                colorScheme="red"
                leftIcon={<Icon as={FiVolumeX} />}
                onClick={stopChoreAssistantSpeech}
              >
                Stop speaking
              </Button>
            ) : null}
            {choreVoiceListening ? (
              <Text fontSize="xs" color="blue.500">
                Listening…
              </Text>
            ) : null}
          </HStack>
          {(choreVoiceListening || choreVoiceSpeaking) && (
            <HStack spacing={2} mt={2}>
              {choreVoiceListening ? (
                <Badge colorScheme="blue" variant="subtle">
                  Listening
                </Badge>
              ) : null}
              {choreVoiceSpeaking ? (
                <Badge colorScheme="purple" variant="subtle">
                  Speaking
                </Badge>
              ) : null}
            </HStack>
          )}
          {choreVoiceReply ? (
            <Text fontSize="xs" color={choreAssistantSubtle} mt={3} whiteSpace="pre-wrap">
              {choreVoiceReply}
            </Text>
          ) : null}
          </CardBody>
        </Card>

        {monthlyStats && (
          <Card
            borderRadius="xl"
            borderWidth="1px"
            borderColor={choreAssistantCardBorder}
            bg={surfaceCard}
            boxShadow="sm"
            overflow="hidden"
          >
            <CardBody py={5} px={{ base: 4, md: 6 }} bg={statCardBg}>
              <Text fontSize="sm" color={choreAssistantSubtle} mb={4}>
                {statsMonthCaption} · use calendar to switch month.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={{ base: 6, md: 8 }}>
                <Stat>
                  <StatLabel fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                    Total tasks
                  </StatLabel>
                  <StatNumber fontSize="3xl">{monthlyStats.totalTasks ?? 0}</StatNumber>
                  <StatHelpText mb={0}>{statsMonthCaption}</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                    Completed
                  </StatLabel>
                  <StatNumber fontSize="3xl">{monthlyStats.totalCompleted ?? 0}</StatNumber>
                  <StatHelpText mb={0}>{statsMonthCaption}</StatHelpText>
                </Stat>
                <Stat>
                  <StatLabel fontSize="xs" textTransform="uppercase" letterSpacing="wider">
                    Completion rate
                  </StatLabel>
                  <StatNumber fontSize="3xl">
                    {((monthlyStats.completionRate || 0) * 100).toFixed(1)}%
                  </StatNumber>
                  <StatHelpText mb={0}>
                    <HStack spacing={1} align="center">
                      <Text as="span" fontSize="xs" color={choreAssistantSubtle}>
                        {statsMonthCaption}
                      </Text>
                      <StatArrow
                        type={monthlyStats.completionRate >= 0.8 ? "increase" : "decrease"}
                      />
                    </HStack>
                  </StatHelpText>
                </Stat>
              </SimpleGrid>
              {choreDailyTrendSeries.length > 0 ? (
                <>
                  <Divider my={6} />
                  <Button
                    colorScheme="blue"
                    variant="solid"
                    size="sm"
                    alignSelf="flex-start"
                    onClick={onDailyTrendOpen}
                  >
                    Show daily trend analytics
                  </Button>
                  <Text fontSize="xs" color={choreAssistantSubtle} mt={2}>
                    Daily trend · IST calendar for the highlighted month above.
                  </Text>
                </>
              ) : null}
            </CardBody>
          </Card>
        )}

        <Modal
          isOpen={isDailyTrendOpen}
          onClose={onDailyTrendClose}
          size="2xl"
          motionPreset="scale"
          scrollBehavior="inside"
          closeOnOverlayClick={false}
          isCentered
        >
          <ModalOverlay bg="blackAlpha.500" />
          <ModalContent
            mx={{ base: 3, md: 6 }}
            borderRadius="2xl"
            bg={surfaceCard}
            borderWidth="1px"
            borderColor={mutedLine}
            maxW="min(92vw, 980px)"
            display="flex"
            flexDirection="column"
            boxShadow="2xl"
          >
            <ModalHeader borderBottomWidth="1px" borderColor={mutedLine} bg={assistantHeaderBg}>
              <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
                <Box>
                  <Text fontWeight="semibold" color={choreAssistantHeading}>
                    Daily trend analytics
                  </Text>
                  <Text fontSize="xs" color={choreAssistantSubtle} mt={1}>
                    IST calendar · pick a range below.
                  </Text>
                  <HStack mt={3} spacing={2} flexWrap="wrap">
                    {DAILY_CHORE_ANALYTICS_RANGES.map((range) => (
                      <Button
                        key={range.id}
                        size="sm"
                        variant={dailyTrendRange === range.id ? "solid" : "outline"}
                        colorScheme="blue"
                        borderRadius="full"
                        onClick={() => setDailyTrendRange(range.id)}
                      >
                        {range.label}
                      </Button>
                    ))}
                  </HStack>
                  {dailyTrendRange === "custom" ? (
                    <HStack mt={3} spacing={3} flexWrap="wrap" align="flex-end">
                      <FormControl maxW="180px">
                        <FormLabel fontSize="xs" color={choreAssistantSubtle}>
                          From
                        </FormLabel>
                        <Input
                          type="date"
                          size="sm"
                          value={dailyTrendCustomStart}
                          onChange={(e) => setDailyTrendCustomStart(e.target.value)}
                          bg={choreAssistantInputBg}
                        />
                      </FormControl>
                      <FormControl maxW="180px">
                        <FormLabel fontSize="xs" color={choreAssistantSubtle}>
                          To
                        </FormLabel>
                        <Input
                          type="date"
                          size="sm"
                          value={dailyTrendCustomEnd}
                          onChange={(e) => setDailyTrendCustomEnd(e.target.value)}
                          bg={choreAssistantInputBg}
                        />
                      </FormControl>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        isDisabled={!dailyTrendCustomStart || !dailyTrendCustomEnd}
                        onClick={() => setDailyTrendRange("custom")}
                      >
                        Apply
                      </Button>
                    </HStack>
                  ) : null}
                </Box>
                <Button size="sm" colorScheme="red" onClick={onDailyTrendClose}>
                  Close
                </Button>
              </Flex>
            </ModalHeader>
            <ModalBody
              flex="1"
              minH={0}
              display="flex"
              flexDirection="column"
              p={{ base: 5, md: 6 }}
              overflowY="auto"
            >
              <Box
                flex="1"
                minH={{
                  base: `${dailyTrendChartHeight}px`,
                  md: `${dailyTrendChartHeight}px`,
                }}
              >
                <Box w="100%" h="100%" overflowX="auto" overflowY="auto">
                  <Box
                    w={chartWidthForPoints(dailyTrendSeries.length, 800)}
                    h={dailyTrendChartHeight}
                  >
                    <AreaChart
                      width={chartWidthForPoints(dailyTrendSeries.length, 800)}
                      height={dailyTrendChartHeight}
                      data={dailyTrendSeries}
                      margin={{ top: 8, right: 12, left: 4, bottom: 4 }}
                    >
                    <defs>
                      <linearGradient id="choreTotalGradModal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.7} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.08} />
                      </linearGradient>
                      <linearGradient id="choreDoneGradModal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.85} />
                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="5 10" stroke={choreChartGrid} />
                    <XAxis
                      dataKey="dayLabel"
                      tick={{ fontSize: 10, fill: choreChartAxis }}
                      interval={chartXAxisIntervalByLength(dailyTrendSeries.length)}
                      minTickGap={8}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: choreChartAxis }}
                      allowDecimals={false}
                      domain={[0, dailyTrendDisplayMax]}
                      allowDataOverflow
                    />
                    <RechartsTooltip
                      formatter={(val) =>
                        typeof val === "number" ? val.toLocaleString("en-IN") : val
                      }
                      contentStyle={{ borderRadius: 12 }}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="totalTasks"
                      name="On the list"
                      stroke="#2563eb"
                      strokeWidth={2}
                      fill="url(#choreTotalGradModal)"
                    />
                    <Area
                      type="monotone"
                      dataKey="completedTasks"
                      name="Completed"
                      stroke="#15803d"
                      strokeWidth={2}
                      fill="url(#choreDoneGradModal)"
                    />
                    </AreaChart>
                  </Box>
                </Box>
              </Box>
            </ModalBody>
            <ModalFooter borderTopWidth="1px" borderColor={mutedLine} bg={assistantHeaderBg}>
              <Text fontSize="xs" color={choreAssistantSubtle}>
                Tip: press Esc to close.
              </Text>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {recurringTemplates.length > 0 && (
          <Card
            borderRadius="xl"
            borderWidth="1px"
            borderLeftWidth="4px"
            borderLeftColor="teal.400"
            borderColor={choreAssistantCardBorder}
            bg={recurringBoxBg}
            boxShadow="sm"
          >
            <CardHeader pb={2}>
              <Text fontWeight="semibold" fontSize="md">
                Repeats every day
              </Text>
              <Text fontSize="xs" color={choreAssistantSubtle} fontWeight="normal">
                Merged into your list from today onward (IST).
              </Text>
            </CardHeader>
            <CardBody pt={0}>
              <VStack align="stretch" spacing={3}>
                {recurringTemplates.map((t, i) => (
                  <Flex
                    key={i}
                    justify="space-between"
                    align="center"
                    gap={3}
                    py={2}
                    px={3}
                    borderRadius="lg"
                    bg={recurringRowBg}
                    borderWidth="1px"
                    borderColor={mutedLine}
                  >
                    <Text fontWeight="medium">
                      {t.choreName}
                      {t.reminderConfigId && (
                        <Badge ml={2} colorScheme="purple" variant="subtle">
                          Reminder
                        </Badge>
                      )}
                    </Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => removeRecurringTemplate(i)}
                    >
                      Remove
                    </Button>
                  </Flex>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}

        <Grid
          templateColumns={{ base: "1fr", lg: "minmax(280px, 340px) 1fr" }}
          gap={{ base: 6, lg: 8 }}
          alignItems="start"
        >
          <GridItem w="full">
            <Card
              variant="outline"
              borderRadius="xl"
              borderColor={choreAssistantCardBorder}
              bg={surfaceCard}
              boxShadow="sm"
              overflow="hidden"
            >
              <CardHeader pb={2} borderBottomWidth="1px" borderColor={mutedLine}>
                <HStack spacing={2}>
                  <Icon as={FiCalendar} color="blue.500" boxSize={5} />
                  <Text fontSize="sm" fontWeight="semibold">
                    Pick a day
                  </Text>
                </HStack>
              </CardHeader>
              <CardBody px={{ base: 2, md: 3 }} py={4}>
                <Box w="full" maxW={{ base: "100%", md: "320px" }} mx={{ base: "auto", md: 0 }}>
                  <Calendar
                    onChange={handleDateChange}
                    value={selectedDate}
                    maxDate={new Date()}
                    className={`react-calendar ${colorMode === "dark" ? "dark" : ""}`}
                  />
                </Box>
              </CardBody>
            </Card>
          </GridItem>

          <GridItem minW={0}>
            <Card
              borderRadius="xl"
              borderWidth="1px"
              borderColor={choreAssistantCardBorder}
              bg={surfaceCard}
              boxShadow="sm"
            >
              <CardHeader
                borderBottomWidth="1px"
                borderColor={mutedLine}
                py={{ base: 2, md: 3 }}
                px={{ base: 3, md: 4 }}
              >
                <VStack spacing={3} align="stretch" w="full">
                  <Flex
                    w="full"
                    gap={3}
                    direction={{ base: "column", lg: "row" }}
                    justify="space-between"
                    align={{ base: "stretch", lg: "flex-start" }}
                  >
                    <Box flex="1" minW={0}>
                      <Text
                        fontSize="xs"
                        fontWeight="bold"
                        color="gray.500"
                        textTransform="uppercase"
                        letterSpacing="0.08em"
                        lineHeight="1.15"
                      >
                        Task list
                      </Text>
                      <Heading
                        as="h2"
                        size="sm"
                        fontWeight="bold"
                        lineHeight="1.25"
                        mt={0.5}
                      >
                        {formattedSelectedDate}
                      </Heading>
                    </Box>
                    <Flex
                      gap={2}
                      flexWrap="wrap"
                      justify={{ base: "flex-start", lg: "flex-end" }}
                      align="center"
                      w={{ base: "full", lg: "auto" }}
                      flexShrink={0}
                    >
                      <Button
                        colorScheme="orange"
                        variant="outline"
                        onClick={handleRemoveAllReminders}
                        isLoading={removingAllReminders}
                        loadingText="Removing"
                        leftIcon={<BellIcon />}
                        size="sm"
                      >
                        Clear reminders
                      </Button>
                      <Button
                        colorScheme="red"
                        variant="outline"
                        onClick={handleDeleteAll}
                        leftIcon={<DeleteIcon />}
                        size="sm"
                      >
                        Delete all chores
                      </Button>
                      <Button
                        colorScheme="blue"
                        leftIcon={<AddIcon />}
                        onClick={openAddChore}
                        isLoading={isLoading}
                        size="sm"
                      >
                        Add chore
                      </Button>
                    </Flex>
                  </Flex>
                  {metaInferred && (
                    <Box
                      py={2}
                      px={3}
                      borderRadius="md"
                      bg={inferredNoteBg}
                      borderWidth="1px"
                      borderColor={inferredNoteBorder}
                      borderLeftWidth="3px"
                      w="full"
                    >
                      <Text fontSize="xs" color={choreAssistantSubtle} lineHeight="short">
                        No saved list for this day — showing a copy from your latest saved day
                        (unchecked).
                      </Text>
                    </Box>
                  )}
                  {structureLocked && !isFutureDay(selectedDate) && (
                    <Text fontSize="xs" color="orange.500" lineHeight="short">
                      This day is more than {STRUCTURE_LOCK_DAYS} days ago: view only — you
                      cannot edit the list or check items off.
                    </Text>
                  )}
                </VStack>
              </CardHeader>
              <CardBody pt={3} pb={5} px={{ base: 4, md: 6 }}>
                <VStack spacing={3} align="stretch">
                  {chores.length === 0 && (
                    <Box
                      textAlign="center"
                      py={12}
                      px={4}
                      borderRadius="xl"
                      borderWidth="1px"
                      borderStyle="dashed"
                      borderColor={choreAssistantCardBorder}
                      bg={emptyStateBg}
                    >
                      <Icon
                        as={FiCheckCircle}
                        boxSize={10}
                        color="gray.400"
                        mb={3}
                        mx="auto"
                      />
                      <Text color="gray.500" fontSize="sm" mb={4}>
                        {isLoading
                          ? "Loading chores…"
                          : "No chores for this day. Add one or use the assistant."}
                      </Text>
                      <Button
                        colorScheme="blue"
                        size="sm"
                        leftIcon={<AddIcon />}
                        onClick={openAddChore}
                        isDisabled={structureLocked || isLoading}
                      >
                        Add chore
                      </Button>
                    </Box>
                  )}
                  {chores.map((chore, index) => (
                    <Box
                      key={`${chore}-${index}`}
                      role="group"
                      p={{ base: 3, md: 4 }}
                      borderWidth="1px"
                      borderRadius="xl"
                      borderLeftWidth="4px"
                      borderLeftColor={
                        statuses[index] ? choreRowDoneBorder : choreRowTodoBorder
                      }
                      bg={choreBoxBg}
                      transition="box-shadow 0.2s ease, transform 0.15s ease"
                      _hover={{
                        boxShadow: "md",
                      }}
                    >
                      <Stack
                        direction={{ base: "column", sm: "row" }}
                        justify="space-between"
                        align={{ base: "stretch", sm: "center" }}
                        spacing={3}
                      >
                        <HStack align="start" spacing={3} flex="1">
                          <Checkbox
                            isChecked={statuses[index]}
                            onChange={() => handleStatusChange(index)}
                            colorScheme="green"
                            isDisabled={!completionAllowed}
                            mt={1}
                            size="lg"
                            alignItems="flex-start"
                          >
                            <Box>
                              <Text
                                fontWeight="medium"
                                textDecoration={
                                  statuses[index] ? "line-through" : "none"
                                }
                                color={statuses[index] ? "gray.500" : "inherit"}
                              >
                                {chore}
                              </Text>
                              {choreNotes[index]?.trim() ? (
                                <Text
                                  fontSize="xs"
                                  color="gray.500"
                                  mt={1}
                                  whiteSpace="pre-wrap"
                                  noOfLines={3}
                                >
                                  {choreNotes[index]}
                                </Text>
                              ) : null}
                            </Box>
                          </Checkbox>
                          {reminderConfigs.some((r) => choreRowIndexEqual(r.index, index)) && (
                            <Badge colorScheme={badgeColorScheme} variant="subtle" borderRadius="md">
                              <HStack spacing={1}>
                                <BellIcon w={3} h={3} />
                                <Text fontSize="xs">Reminder</Text>
                              </HStack>
                            </Badge>
                          )}
                        </HStack>
                        <HStack spacing={1} justify="flex-end">
                          <IconButton
                            icon={<EditIcon />}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleChoreEdit(index)}
                            aria-label="Edit chore"
                            isDisabled={structureLocked}
                          />
                          <IconButton
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleChoreDelete(index)}
                            aria-label="Delete chore"
                            isDisabled={structureLocked}
                          />
                        </HStack>
                      </Stack>
                    </Box>
                  ))}
                </VStack>
              </CardBody>
            </Card>
          </GridItem>
        </Grid>

        </VStack>
      </Container>
    </Box>

    <ChoreEditModal
          isOpen={isOpen}
          onClose={() => {
            setEditingChore(null);
            onClose();
          }}
          editingChore={editingChore}
          onSubmit={handleChoreSubmit}
          showRecurringOption={isTodayYmd(selectedDate) && canEditChoreStructure(selectedDate)}
        />

    <Modal isOpen={isAnalyticsOpen} onClose={onAnalyticsClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Chore analytics & consistency</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <RadioGroup
                  value={analyticsPreset}
                  onChange={setAnalyticsPreset}
                >
                  <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                    <Radio value="week">Last 7 days</Radio>
                    <Radio value="month">Last 30 days</Radio>
                    <Radio value="custom">Custom range</Radio>
                  </Stack>
                </RadioGroup>

                {analyticsPreset === "custom" && (
                  <HStack spacing={3} flexWrap="wrap">
                    <FormControl maxW="200px">
                      <FormLabel fontSize="sm">From</FormLabel>
                      <Input
                        type="date"
                        value={customStart}
                        onChange={(e) => setCustomStart(e.target.value)}
                        size="sm"
                      />
                    </FormControl>
                    <FormControl maxW="200px">
                      <FormLabel fontSize="sm">To</FormLabel>
                      <Input
                        type="date"
                        value={customEnd}
                        onChange={(e) => setCustomEnd(e.target.value)}
                        size="sm"
                      />
                    </FormControl>
                    <Button mt={6} size="sm" onClick={loadAnalytics}>
                      Apply range
                    </Button>
                  </HStack>
                )}

                <Divider />

                {analyticsStats && (
                  <>
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                      <Stat>
                        <StatLabel>Total tasks</StatLabel>
                        <StatNumber>{analyticsStats.totalTasks ?? 0}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Completed</StatLabel>
                        <StatNumber>{analyticsStats.totalCompleted ?? 0}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Pending</StatLabel>
                        <StatNumber>{analyticsStats.pendingTotal ?? 0}</StatNumber>
                      </Stat>
                      <Stat>
                        <StatLabel>Completion rate</StatLabel>
                        <StatNumber>
                          {((analyticsStats.completionRate || 0) * 100).toFixed(1)}%
                        </StatNumber>
                        <StatHelpText>
                          <StatArrow
                            type={
                              analyticsStats.completionRate >= 0.8
                                ? "increase"
                                : "decrease"
                            }
                          />
                        </StatHelpText>
                      </Stat>
                    </SimpleGrid>

                    <DashboardDataTable
                      title="Daily breakdown"
                      description={`${analyticsStats.calendarDaysInRange ?? 0} days · ${(analyticsStats.dailyStats || []).length} rows`}
                      maxH={{ base: "none", md: "min(50vh, 400px)" }}
                    >
                      <Table
                        variant="simple"
                        size="sm"
                        colorScheme="gray"
                        sx={{ borderCollapse: "separate", borderSpacing: 0 }}
                      >
                        <Thead
                          position="sticky"
                          top={0}
                          zIndex={1}
                          bg={tableStyles.tableHeaderBg}
                          backdropFilter="blur(8px)"
                          borderBottomWidth="1px"
                          borderColor={tableStyles.borderColor}
                        >
                          <Tr>
                            <Th {...tableStyles.thProps}>Date</Th>
                            <Th {...tableStyles.thProps} isNumeric>
                              Total
                            </Th>
                            <Th {...tableStyles.thProps} isNumeric>
                              Done
                            </Th>
                            <Th {...tableStyles.thProps} isNumeric>
                              Pending
                            </Th>
                            <Th {...tableStyles.thProps} isNumeric>
                              Rate
                            </Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {(analyticsStats.dailyStats || []).map((day, index) => {
                            const rate = (day.completionRate || 0) * 100;
                            const rateTone =
                              rate >= 80
                                ? "green.600"
                                : rate >= 50
                                  ? "yellow.700"
                                  : "red.500";
                            const total = day.totalTasks ?? 0;
                            const done = day.completedTasks ?? 0;
                            const pending =
                              day.pendingTasks !== undefined && day.pendingTasks !== null
                                ? day.pendingTasks
                                : Math.max(0, total - done);
                            return (
                              <Tr key={index} sx={tableStyles.trHoverSx}>
                                <Td py={3} fontSize="sm">
                                  {day.date ? (
                                    <Tooltip
                                      isDisabled={!day.filledFromPrevious}
                                      label="No chore save that day — same task count as your last saved list, all unchecked"
                                      placement="top"
                                      fontSize="xs"
                                    >
                                      <Text as="span" fontStyle={day.filledFromPrevious ? "italic" : "normal"}>
                                        {new Date(day.date).toLocaleDateString()}
                                      </Text>
                                    </Tooltip>
                                  ) : (
                                    "—"
                                  )}
                                </Td>
                                <Td isNumeric py={3} fontSize="sm" fontVariantNumeric="tabular-nums">
                                  {total}
                                </Td>
                                <Td isNumeric py={3} fontSize="sm" fontVariantNumeric="tabular-nums">
                                  {done}
                                </Td>
                                <Td isNumeric py={3} fontSize="sm" fontVariantNumeric="tabular-nums">
                                  {pending}
                                </Td>
                                <Td
                                  isNumeric
                                  py={3}
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  fontVariantNumeric="tabular-nums"
                                  color={rateTone}
                                >
                                  {rate.toFixed(1)}%
                                </Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    </DashboardDataTable>
                  </>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button onClick={onAnalyticsClose}>Close</Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

      <ShareCollaborationModal
        isOpen={shareChoreModal.isOpen}
        onClose={shareChoreModal.onClose}
        titleLabel="Share this day’s chore list"
        kind="DAILY_CHORE"
        anchor={{
          dailyChoreAnchorId: primaryDailyChoreDocId || undefined,
        }}
      />
    </>
  );
};

const ChoreEditModal = ({
  isOpen,
  onClose,
  editingChore,
  onSubmit,
  showRecurringOption,
}) => {
  const [choreData, setChoreData] = useState({
    choreName: "",
    choreNote: "",
    status: false,
    applyRecurring: false,
    reminder: {
      type: "",
      time: "09:00",
    },
  });
  const toast = useToast();

  useEffect(() => {
    if (editingChore) {
      const br = editingChore.reminder || {};
      setChoreData({
        choreName: editingChore?.choreName || "",
        choreNote: editingChore?.choreNote ?? "",
        status: editingChore?.status || false,
        applyRecurring: false,
        reminder: {
          type: br.type || "",
          time: br.time || "09:00",
          dayOfWeek: br.dayOfWeek || "",
          dayOfMonth:
            br.dayOfMonth != null && !Number.isNaN(Number(br.dayOfMonth))
              ? Number(br.dayOfMonth)
              : "",
          ...(br._id != null ? { _id: br._id } : {}),
        },
      });
    } else {
      setChoreData({
        choreName: "",
        choreNote: "",
        status: false,
        applyRecurring: false,
        reminder: {
          type: "",
          time: "09:00",
        },
      });
    }
  }, [editingChore]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!choreData.choreName?.trim()) {
      toast({
        title: "Error saving chore",
        description: "Please enter chore name",
        status: "error",
        duration: 3000,
      });
      return;
    }

    if (choreData.reminder?.type) {
      if (
        choreData.reminder.type === "weekly" &&
        !choreData.reminder.dayOfWeek
      ) {
        toast({
          title: "Error",
          description: "Pick a weekday for weekly reminders.",
          status: "error",
          duration: 3000,
        });
        return;
      }
      if (
        choreData.reminder.type === "monthly" &&
        (choreData.reminder.dayOfMonth == null ||
          choreData.reminder.dayOfMonth < 1 ||
          choreData.reminder.dayOfMonth > 31)
      ) {
        toast({
          title: "Error",
          description: "Enter a valid day of month (1–31).",
          status: "error",
          duration: 3000,
        });
        return;
      }
    }

    onSubmit(choreData);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{editingChore ? "Edit Chore" : "Add New Chore"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Chore Name</FormLabel>
              <Input
                value={choreData.choreName}
                onChange={(e) =>
                  setChoreData({ ...choreData, choreName: e.target.value })
                }
                placeholder="Enter chore name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Notes (optional)</FormLabel>
              <Textarea
                value={choreData.choreNote || ""}
                onChange={(e) =>
                  setChoreData({ ...choreData, choreNote: e.target.value })
                }
                placeholder="Extra details, links, or reminders for this line…"
                rows={3}
                maxLength={4000}
              />
            </FormControl>

            {showRecurringOption && (
              <Checkbox
                isChecked={choreData.applyRecurring}
                onChange={(e) =>
                  setChoreData({ ...choreData, applyRecurring: e.target.checked })
                }
              >
                Repeat every day from today (merge into future days)
              </Checkbox>
            )}

            <FormControl>
              <FormLabel>Reminder</FormLabel>
              <Select
                value={choreData.reminder.type || ""}
                onChange={(e) =>
                  setChoreData({
                    ...choreData,
                    reminder: { ...choreData.reminder, type: e.target.value },
                  })
                }
              >
                <option value="">No reminder (optional)</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </Select>
            </FormControl>

            {choreData.reminder.type && (
              <FormControl>
                <FormLabel>Reminder Time</FormLabel>
                <Input
                  type="time"
                  value={choreData.reminder.time}
                  onChange={(e) =>
                    setChoreData({
                      ...choreData,
                      reminder: { ...choreData.reminder, time: e.target.value },
                    })
                  }
                />
              </FormControl>
            )}

            {choreData.reminder.type === "weekly" && (
              <FormControl>
                <FormLabel>Day of Week</FormLabel>
                <Select
                  value={choreData.reminder.dayOfWeek || ""}
                  onChange={(e) =>
                    setChoreData({
                      ...choreData,
                      reminder: {
                        ...choreData.reminder,
                        dayOfWeek: e.target.value,
                      },
                    })
                  }
                >
                  {[
                    "Monday",
                    "Tuesday",
                    "Wednesday",
                    "Thursday",
                    "Friday",
                    "Saturday",
                    "Sunday",
                  ].map((day) => (
                    <option key={day} value={day}>
                      {day}
                    </option>
                  ))}
                </Select>
              </FormControl>
            )}

            {choreData.reminder.type === "monthly" && (
              <FormControl>
                <FormLabel>Day of Month</FormLabel>
                <Input
                  type="number"
                  min={1}
                  max={31}
                  value={choreData.reminder.dayOfMonth || ""}
                  onChange={(e) =>
                    setChoreData({
                      ...choreData,
                      reminder: {
                        ...choreData.reminder,
                        dayOfMonth: parseInt(e.target.value, 10),
                      },
                    })
                  }
                />
              </FormControl>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={handleSubmit}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DailyChoreManager;
